<template>
  <div class="home" v-if="content">
    <Model
      v-if="livestream"
      :livestream="livestream"
      :key="hash(livestream)"
      class="cursor-none pt-3 md:pt-0"
    />

    <Separator pattern="grid" class="w-full h-8 border-t border-white" />
    <Separator
      id="about"
      pattern="grid"
      class="w-full h-8 border-t border-white border-b"
    />

    <span
      v-waypoint="{
        active: true,
        callback: contentWaypoint,
        options: intersectionOptions,
      }"
    ></span>

    <img
      src="../assets/yca-crest.svg"
      class="mx-auto my-24"
      alt="Young Curators Academy - Crest Ornament"
    />

    <div class="text-2xl max-w-4xl text-center mx-auto px-6 md:px-0">
      <div v-html="content.about" />

      <div class="my-16 space-y-16">
        <p class="mt-32">
          A project by
          <a
            href="https://gorki.de"
            class="border-white border-b hover:border-full-red"
            target="_blank"
            >Maxim Gorki Theater</a
          >
        </p>
        <p class="mt-32">Directed by Ong Keng Sen</p>
        <p class="my-16">Hosted by Ong Keng Sen, Silke Bake and Erden Kosova</p>
      </div>

      <div
        class="md:grid grid-cols-2 grid-space-8 gap-12 space-y-12 md:space-y-0"
      >
        <div>
          <div class="uppercase text-sm mb-2">Digital Production</div>
          <div class="text-2xl">Rabelle Ramez Erian</div>
        </div>
        <div>
          <div class="uppercase text-sm mb-2">Curatorial Support</div>
          <div class="text-2xl">Anne Diestelkamp</div>
        </div>
        <div>
          <div class="uppercase text-sm mb-2">Production Management</div>
          <div class="text-2xl">Lucia Leyser</div>
        </div>
        <div>
          <div class="uppercase text-sm mb-2">Website</div>
          <div class="text-2xl">Alan Woo</div>
        </div>
      </div>
      <div class="mt-12">
        <div class="uppercase text-sm mb-2">
          Livestream Technical Design & Production
        </div>
        <div class="text-2xl">Yap Seok Hui, ARTFACTORY</div>

        <div class="mt-12 text-sm">
          As part of 5. Berliner Herbstsalon.<br />
          Funded by the State of Berlin, Senate Department for Culture and
          Europe.<br />
          In collaboration and with the support of T:>Works International
          Curators Academy.
        </div>
      </div>
    </div>

    <div id="calendar" class="sticky mt-48">
      <Separator
        pattern="oval"
        class="mt-24 w-full h-20 border-t border-white border-b"
      />

      <div class="mono uppercase -mt-20 flex items-center w-auto h-20">
        <div
          class="
            bg-black
            flex
            items-center
            mx-auto
            px-8
            text-2xl
            h-16
            tracking-widest
          "
        >
          2021
        </div>
      </div>
    </div>

    <div class="flex flex-col md:grid grid-cols-3 mono">
      <div
        v-for="project in content.projects"
        :key="`project-${project.id}`"
        class="
          md:text-center
          border-white
          md:border-r
          last:border-none
          relative
          mt-12
          md:mt-0
        "
      >
        <a
          v-if="project.items.length && project.items[0].hasOwnProperty('uri')"
          :href="`/#calendar/${project.items[0].uri}`"
          class="block uppercase py-6 mx-6 md:mx-0"
        >
          <h2
            class="text-3xl mb-3 md:text-base md:mb-0"
            v-html="project.title || project.display_title"
          ></h2>
          <h3 v-html="project.dates" />
        </a>
        <div v-else class="uppercase py-6 mx-6 md:mx-0">
          <h2
            class="text-3xl mb-3 md:text-base md:mb-0"
            v-html="project.title || project.display_title"
          ></h2>
          <h3 v-html="project.dates" />
        </div>
        <a
          v-if="project.items.length && project.items[0].hasOwnProperty('uri')"
          class="md:hidden py-2 px-3 border border-white inline-block mx-6"
          :href="`/#calendar/${project.items[0].uri}`"
        >
          View Schedule ↴
        </a>
        <div
          class="flex flex-col h-full"
          :class="{ 'justify-between': readMore }"
        >
          <div
            class="
              border-white border-dotted
              md:border-t
              p-6
              md:p-12
              overflow-hidden
              relative
              read-more-toggle
              cursor-pointer
            "
            @click="readMore = !readMore"
            :class="{ 'md:h-64': !readMore, 'mb-16': readMore }"
          >
            <div v-html="project.text" class="flex-grow text" />

            <ul v-if="project.people" class="text-lg mt-6 hidden md:block">
              <li
                v-for="(person, idx) in sortBy(project.people, ['sort'])"
                :key="idx"
                class="comma-separated inline"
              >
                <router-link :to="person.uri"
                  >{{ person.name
                  }}<span v-if="person.location">
                    ({{ person.location }})</span
                  ></router-link
                >
              </li>
            </ul>

            <div
              v-if="!readMore"
              class="
                read-more
                absolute
                left-0
                bottom-0
                z-10
                w-full
                text-center
                mb-4
              "
            >
              Read More
            </div>
            <div
              v-if="!readMore"
              class="hidden md:block fade absolute bottom-0 left-0 right-0 h-48"
            />
          </div>
          <div
            class="border-white border-b hidden md:block"
            :class="{ 'absolute bottom-0 left-0 right-0': readMore }"
          >
            <div class="border-white border-dotted border-t flex">
              <div
                v-for="event in project.items"
                :key="event.id"
                class="
                  flex-auto
                  border-dotted border-r border-white
                  last:border-none
                "
              >
                <a
                  v-if="event.active_calendar == 'true'"
                  :href="`#calendar/${event.uri}`"
                >
                  <h4
                    v-if="event.display_date"
                    v-text="event.display_date"
                    class="py-3"
                  />
                </a>
                <h4 v-else v-text="event.display_date" class="py-3" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- <transition name="fade">
      <div
        v-if="showHeader"
        class="z-10 fixed top-0 text-center pt-6 w-screen pointer-events-none"
      >
        {{ format(new Date(event.date), 'MMM dd, yyyy') }} -
        {{ event.display_title }}
      </div>
    </transition> -->

    <!-- EVENTS -->
    <div
      v-for="(event, idx) in events"
      class="max-w-6xl mx-auto mono px-6"
      :id="`calendar/${event.uri}`"
      :ref="`calendar/${event.uri}`"
      :key="idx"
    >
      <div
        class="mt-32 pt-24 md:text-6xl mb-12 flex"
        v-if="event.active_calendar == 'true'"
      >
        <div class="w-1/3 uppercase hidden md:block">
          {{ format(new Date(event.date), 'MMM dd') }}
        </div>
        <div class="md:w-2/3 relative">
          <span
            class="md:hidden mb-6 text-black py-1 px-5 bg-full-red inline-block"
          >
            {{ format(new Date(event.date), 'MMM dd, yyyy') }}
          </span>
          <h3 v-html="event.display_title" class="text-5xl"></h3>
          <span
            class="absolute"
            v-waypoint="{
              active: true,
              callback: eventWaypoint,
              options: intersectionOptions,
            }"
          ></span>
          <div v-html="event.text" class="hidden md:block text-lg mt-12"></div>
        </div>
      </div>

      <div v-if="event.active_calendar == 'true'">
        <div
          v-for="(event, idx) in event.events"
          :key="`event-${idx}`"
          class="flex mb-12 items-start"
        >
          <div class="w-1/3 hidden md:flex items-center mono">
            <span
              >{{ event.start.slice(0, -3) }} -
              {{ event.end.slice(0, -3) }} CEST</span
            >
            <span
              class="flex flex-grow border-dotted border-white border-b-4 mx-6"
            ></span>
          </div>
          <div class="md:w-2/3">
            <div class="md:hidden mb-3">
              <span
                >{{ event.start.slice(0, -3) }} -
                {{ event.end.slice(0, -3) }} CEST</span
              >
            </div>
            <div v-if="event.people.length" class="text-sm uppercase mb-3 mono">
              <span
                v-for="person in event.people"
                :key="`${event.uri}-${person.id}`"
                class="tracking-widest comma-separated"
                ><router-link :to="`${person.uri}`">{{
                  person.display_title || person.title
                }}</router-link></span
              >
            </div>
            <div class="text-lg">
              <!-- blocks the agora event pages -->
              <router-link
                v-if="!event.parent.includes('Agora') && event.link"
                :to="event.link"
              >
                <h4 class="text-3xl md:text-4xl leading-none md:leading-snug">
                  <span v-html="event.title"></span>
                  <sup
                    v-if="event.video"
                    class="text-full-red ml-3 uppercase text-sm"
                    >Watch Video</sup
                  >
                </h4>
              </router-link>
              <h4
                v-else
                class="text-3xl md:text-4xl leading-none md:leading-snug"
              >
                <span v-html="event.title"></span>
              </h4>
              <div v-html="event.description" class="mt-3" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div id="people" class="sticky mt-48">
      <Separator
        pattern="oval"
        class="mt-24 w-full h-20 border-t border-white border-b"
      />

      <div class="mono uppercase -mt-20 flex items-center w-auto h-20">
        <div
          class="
            bg-black
            flex
            items-center
            mx-auto
            px-8
            text-2xl
            h-16
            tracking-widest
          "
        >
          YCA 2021
        </div>
      </div>
    </div>

    <ul
      class="
        text-lg
        max-w-2xl
        mx-auto
        text-center
        my-16
        md:my-32
        space-y-3
        mx-6
        md:mx-auto
      "
    >
      <li v-for="(person, idx) in people" :key="idx" class="md:text-3xl">
        <router-link :to="person.uri"
          >{{ person.title
          }}<span v-if="person.location">
            ({{ person.location }})</span
          ></router-link
        >
      </li>
    </ul>

    <Separator
      pattern="oval"
      class="mt-48 w-full h-20 border-t border-white border-b"
    />
    <Separator
      pattern="oval"
      class="mt-3 w-full h-20 border-t border-white border-b"
    />
    <Separator
      pattern="crest"
      class="mt-3 w-full h-16 border-t border-white border-b"
    />
    <Separator pattern="brick" class="mt-3 w-full h-32 border-t border-white" />
    <Separator
      pattern="brick"
      class="w-full h-32 border-t border-white border-b"
    />
  </div>
</template>

<script>
// @ is an alias to /src
import Model from '@/components/Model.vue'
import Separator from '@/components/Separator.vue'

import { isPast, format, parse, formatDistanceToNow } from 'date-fns'
import hash from 'object-hash'
import { sortBy, uniqBy } from 'lodash'

export default {
  name: 'Home',
  props: ['content', 'livestream'],
  data() {
    return {
      intersectionOptions: {
        root: null,
        rootMargin: '0px 0px 0px 0px',
        threshold: [0],
      },
      showHeader: false,
      readMore: false,
    }
  },
  components: {
    Model,
    Separator,
  },
  computed: {
    events() {
      return this.$props.content.projects.map((x) => x.items).flat()
    },
    live() {
      return this.$props.livestream.livestream_active == 'true'
    },
    distance() {
      if (isPast(new Date(this.$props.livestream.livestream_start))) {
        return 'Soon'
      } else {
        return (
          'in ' +
          formatDistanceToNow(new Date(this.$props.livestream.livestream_start))
        )
      }
    },
    people() {
      let people = this.events
        .map((x) => x.events)
        .flat()
        .map((y) => y.people)
        .flat()

      let unique = uniqBy(people, 'id')

      return sortBy(unique, ['sort'])
    },
  },
  methods: {
    format,
    parse,
    hash,
    sortBy,
    contentWaypoint({ going, direction }) {
      // fires on exit, so stop that :)
      if (this._isBeingDestroyed) return

      if (
        going === this.$waypointMap.GOING_OUT &&
        direction === this.$waypointMap.DIRECTION_TOP
      ) {
        this.$emit('hideButtons', true)
      } else {
        this.$emit('hideButtons', false)
      }
    },
    eventWaypoint({ going, direction }) {
      // fires on exit, so stop that :)
      if (this._isBeingDestroyed) return

      if (
        going === this.$waypointMap.GOING_OUT &&
        direction === this.$waypointMap.DIRECTION_TOP
      ) {
        this.showHeader = true
      } else {
        this.showHeader = false
      }
    },
    getPeople(event) {
      return sortBy(event.events.map((x) => x.people).flat(), ['sort'])
    },
  },
}
</script>

<style lang="css" scoped>
.fade {
  background: rgb(0, 0, 0);
  background: linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 100%);
}

.read-more {
  bottom: -3em;
  transition: all 0.2s ease-out;
}

.read-more-toggle:hover .read-more {
  bottom: 0;
}

a.router-link-exact-active {
  @apply text-full-green;
}
</style>
